import React, { FC, useContext, useEffect } from 'react';
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  useToast,
} from '@chakra-ui/react';
import InfolinoContext from '../../../context/InfolinoContext';
import {
  CreateInfolinoFeedbackInput,
  Mutation,
  MutationCreateInfolinoFeedbackArgs,
  ParagraphInfolinoFeedbackFormularFragmentFragment,
} from '../../../generated/types';
import { useForm } from 'react-hook-form';
import { useMutation } from '@apollo/client';
import { CREATE_INFOLINO_FEEDBACK_QUERY } from '../../../queries';
import { CustomStarEmpty, CustomStarFilled } from '../../../config/icons';
import Rating from 'react-rating';

interface FeedbackFormValues {
  firstname: string;
  lastname: string;
  room: string;
  email: string;
  ratings: Record<string, number>;
  comment: string;
}

type InfolinoFeedbackFormularProps = {
  data: ParagraphInfolinoFeedbackFormularFragmentFragment;
};

const InfolinoFeedbackFormular: FC<InfolinoFeedbackFormularProps> = ({
  data,
}) => {
  const infolinoContext = useContext(InfolinoContext);
  const [createFeedback, { loading }] = useMutation<
    Mutation,
    MutationCreateInfolinoFeedbackArgs
  >(CREATE_INFOLINO_FEEDBACK_QUERY);
  const toast = useToast();

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    setError,
  } = useForm<FeedbackFormValues>();

  const ratings = watch('ratings');

  const onSubmit = async (values: FeedbackFormValues) => {
    const feedbackInputApi: CreateInfolinoFeedbackInput = {
      hotel: `${infolinoContext?.hotel?.id}`,
      firstname: values.firstname,
      lastname: values.lastname,
      email: values.email,
      room: values.room,
      comment: values.comment,
      ratings: Object.keys(values.ratings).map((key) => ({
        key,
        value: values.ratings[key],
      })),
      frontend: 'Infolino',
      paragraphId: data.id,
    };

    const result = await createFeedback({
      variables: {
        data: feedbackInputApi,
      },
    });

    if (result.data) {
      if (result.data.createInfolinoFeedback?.created) {
        toast({
          title: 'Feedback gesendet',
          description: 'Deine Feedback wurde gesendet. Vielen Dank!',
          status: 'success',
          duration: 6000,
          isClosable: true,
        });

        reset();
        return;
      }
    } else {
      if (result.data.createInfolinoFeedback?.errors) {
        result.data.createInfolinoFeedback?.errors.forEach((error) => {
          setError(error.field as any, error);
        });
      }
    }

    toast({
      title: 'Fehler',
      description:
        'Bei der Übermittlung ist ein Fehler aufgetreten. Bitte versuche es später erneut.',
      status: 'error',
      duration: 6000,
      isClosable: true,
    });
  };

  useEffect(() => {
    register('ratings');
  }, []);

  return (
    <Box>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Flex marginTop="4">
          <FormControl flex="1" isRequired>
            <FormLabel fontSize="sm" htmlFor="firstname">
              Vorname
            </FormLabel>
            <Input
              variant="outline"
              size="sm"
              type="text"
              id="firstname"
              name="firstname"
              {...register('firstname', {
                required: true,
              })}
            />
          </FormControl>
          <FormControl flex="1" isRequired marginLeft="2">
            <FormLabel fontSize="sm" htmlFor="lastname">
              Nachname
            </FormLabel>
            <Input
              size="sm"
              type="text"
              id="lastname"
              name="lastname"
              {...register('lastname', {
                required: true,
              })}
            />
          </FormControl>
        </Flex>
        <Flex marginTop="4">
          <FormControl flex="1" isRequired>
            <FormLabel fontSize="sm" htmlFor="room">
              Zimmernummer
            </FormLabel>
            <Input
              variant="outline"
              size="sm"
              type="text"
              id="room"
              name="room"
              {...register('room', {
                required: true,
              })}
            />
          </FormControl>
          <FormControl flex="1" isRequired marginLeft="2">
            <FormLabel fontSize="sm" htmlFor="email">
              E-Mail
            </FormLabel>
            <Input
              size="sm"
              type="email"
              id="email"
              name="email"
              {...register('email', {
                required: true,
              })}
            />
          </FormControl>
        </Flex>

        {data.ratingCategories?.map((categoryName, i) => {
          return (
            <FormControl marginTop={4} flex="1" key={i}>
              <FormLabel display="inline-block" fontSize="sm" htmlFor="body">
                {categoryName}
              </FormLabel>
              <Rating
                initialRating={(ratings && ratings[categoryName]) || 0}
                onChange={(rating) => {
                  setValue('ratings', {
                    ...ratings,
                    [categoryName]: rating,
                  });
                }}
                emptySymbol={
                  <CustomStarEmpty fontSize="2xl" fill="yellow.500" />
                }
                fullSymbol={
                  <CustomStarFilled fontSize="2xl" fill="yellow.500" />
                }
              />
            </FormControl>
          );
        })}
        <FormControl marginTop={4} flex="1">
          <FormLabel fontSize="sm" htmlFor="comment">
            Nachricht
          </FormLabel>
          <Textarea
            size="sm"
            id="comment"
            name="comment"
            {...register('comment')}
          />
        </FormControl>
        <Box mt={4}>
          <Button
            disabled={loading}
            isLoading={loading}
            width="100%"
            variant="outline"
            type="submit"
            colorScheme="blue"
          >
            Abschicken
          </Button>
        </Box>
      </form>
    </Box>
  );
};

export default InfolinoFeedbackFormular;
